import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageCircle, X, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { useMessageWebSocket } from "@/hooks/useMessageWebSocket";
import MessageBubble from "@/components/MessageBubble";

export default function MessageDrawer({ jobId, otherUser, title, onClose }) {
  const { user } = useAuth();
  const { messages, live, send } = useMessageWebSocket(jobId);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    const t = setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
    return () => clearTimeout(t);
  }, [messages.length]);

  const handleSend = async () => {
    setSending(true);
    try {
      await send(text, otherUser);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-end" data-testid="message-drawer">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-white border-2 border-ink w-full md:max-w-md h-[80vh] md:h-[600px] flex flex-col">
        <div className="border-b-2 border-ink p-4 flex items-center justify-between bg-ink text-acid">
          <div className="min-w-0 flex-1">
            <div className="overline text-acid flex items-center gap-2">
              // Conversation
              <span data-testid="ws-status" className="inline-flex items-center gap-1">
                {live ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3 opacity-60" />}
                <span className="text-[10px]">{live ? "live" : "polling"}</span>
              </span>
            </div>
            <div className="font-display font-bold uppercase text-lg mt-1 leading-tight truncate">{title}</div>
          </div>
          <button onClick={onClose} className="bg-acid text-ink border-2 border-acid w-8 h-8 flex items-center justify-center shrink-0 ml-2" data-testid="msg-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-bone">
          {messages.length === 0 && (
            <div className="text-center text-sm font-mono uppercase tracking-widest text-muted-foreground py-12">
              <MessageCircle className="w-6 h-6 mx-auto mb-2" />
              No messages yet
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.message_id} message={m} mine={m.from_user_id === user?.user_id} />
          ))}
        </div>
        <div className="border-t-2 border-ink p-3 flex gap-2 bg-white">
          <input
            className="brut-input flex-1"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={onKeyDown}
            data-testid="msg-input"
          />
          <button onClick={handleSend} disabled={sending || !text.trim()} className="brut-btn-primary" data-testid="msg-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
